import { useEffect, useState, useRef, useCallback } from 'react';

/* eslint-disable react-hooks/exhaustive-deps */
const useMountEffect = callback => useEffect(callback, []);

const useIsMounted = () => {
  const [isMounted, setIsMounted] = useState(false);
  useMountEffect(() => {
    setIsMounted(true);
  });
  return isMounted;
};

/* istanbul ignore file */

// This is useful when you want to fire an effect only after you've mounted.
// Useful for example if you have memoized objects you want to export from
// custom hooks but only when they change after the initial construction
const useUpdateEffect = (effect, deps) => {
  const mounted = useRef(false);
  useEffect(() => {
    if (mounted.current) {
      return effect();
    }
    mounted.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  // Reset the mounted flag on real unmount. Without this, React 18 StrictMode's
  // dev-only mount double-invoke (setup -> cleanup -> setup) leaves
  // `mounted.current` true after the first setup, so the second setup runs
  // `effect()` on initial mount -- defeating the "skip first render" guard and
  // firing update effects (e.g. useOffsetPagination's onPageChange) spuriously.
  // Resetting here means the simulated unmount flips it back to false so the
  // second setup correctly takes the skip branch.
  useEffect(() => () => {
    mounted.current = false;
  }, []);
};

// https://www.joshwcomeau.com/snippets/react-hooks/use-timeout/
function useTimeout(callback, delay) {
  const timeoutRef = useRef(null);
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    const tick = () => savedCallback.current();
    if (typeof delay === 'number') {
      timeoutRef.current = window.setTimeout(tick, delay);
      return () => window.clearTimeout(timeoutRef.current);
    }
  }, [delay]);
  return timeoutRef;
}

/**
 * Hook that returns the previous value of a variable.
 * Useful for comparing current vs previous props/state in effects.
 *
 * @template T - The type of the value being tracked
 * @param value - The current value to track
 * @param defaultValue - The initial value to return on first render (defaults to null)
 * @returns The previous value (or defaultValue on first render)
 *
 * @example
 * ```tsx
 * const [count, setCount] = useState(0)
 * const prevCount = usePrevious(count)
 *
 * useEffect(() => {
 *   if (prevCount !== count) {
 *     console.log(`Count changed from ${prevCount} to ${count}`)
 *   }
 * }, [count, prevCount])
 * ```
 */
const usePrevious = (value, defaultValue = null) => {
  const ref = useRef(defaultValue);
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
};

function useInterval(callback, interval) {
  const intervalRef = useRef(null);
  const savedCallback = useRef();
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    const handler = (...args) => savedCallback.current(...args);
    if (interval) {
      intervalRef.current = setInterval(handler, interval);
      return () => clearInterval(intervalRef.current);
    }
  }, [interval]);
  return intervalRef;
}

function useOnce(initializer) {
  const [value] = useState(initializer);
  return value;
}

/**
 * A hook for navigating through paginated items (rows, cards, etc.) with automatic page transitions
 *
 * @param config - Configuration options for row navigation
 * @returns Navigation state and methods
 */
const useRowNavigation = ({
  // @ts-ignore not using pageSize currently, but keeping it for future use
  pageSize,
  // eslint-disable-line
  currentPage,
  totalPages,
  onPageChange,
  itemCount,
  onItemSelect,
  initialItemIndex = 0,
  isLoading = false
}) => {
  // Track navigation lock state with ref for immediate blocking
  const navigationLockedRef = useRef(false);
  // Initialize active item index
  const [activeItemIndex, setActiveItemIndex] = useState(initialItemIndex);
  // Store direction of navigation to set proper item index on page change
  const navigationDirectionRef = useRef(null);
  // When page changes, set appropriate item index
  useEffect(() => {
    if (itemCount <= 0) {
      setActiveItemIndex(0);
      navigationDirectionRef.current = null;
      return;
    }
    if (navigationDirectionRef.current === 'backward') {
      // When going backward, select last item of new page
      setActiveItemIndex(itemCount - 1);
    } else {
      // When going forward or initial load, select first item
      setActiveItemIndex(0);
    }
    // Reset direction after use
    navigationDirectionRef.current = null;
  }, [currentPage, itemCount]);
  // Unlock navigation when loading completes
  useEffect(() => {
    if (!isLoading && navigationLockedRef.current) {
      navigationLockedRef.current = false;
    }
  }, [isLoading]);
  // Update active item and trigger callbacks
  const updateActiveItem = useCallback(newIndex => {
    setActiveItemIndex(newIndex);
    if (onItemSelect) {
      onItemSelect(newIndex);
    }
  }, [onItemSelect]);
  // Navigate to next item
  const gotoNextItem = useCallback(() => {
    // Immediate block - either we locked it OR external loading
    if (navigationLockedRef.current || isLoading) {
      return;
    }
    // If we're at the last item on the current page
    if (activeItemIndex === itemCount - 1) {
      // If we're not on the last page, go to next page
      if (currentPage < totalPages) {
        const nextPage = currentPage + 1;
        // IMMEDIATELY lock navigation (synchronous ref update)
        navigationLockedRef.current = true;
        // Set navigation direction
        navigationDirectionRef.current = 'forward';
        onPageChange(nextPage);
        // The effect will handle setting activeItemIndex when page changes
      }
      // Otherwise, we're at the last item of the last page - do nothing
    } else {
      // Just go to next item on current page
      updateActiveItem(activeItemIndex + 1);
    }
  }, [isLoading, activeItemIndex, itemCount, currentPage, totalPages, onPageChange, updateActiveItem]);
  // Navigate to previous item
  const gotoPreviousItem = useCallback(() => {
    // Immediate block - either we locked it OR external loading
    if (navigationLockedRef.current || isLoading) {
      return;
    }
    // If we're at the first item on the current page
    if (activeItemIndex === 0) {
      // If we're not on the first page, go to previous page
      if (currentPage > 1) {
        const prevPage = currentPage - 1;
        // IMMEDIATELY lock navigation (synchronous ref update)
        navigationLockedRef.current = true;
        // Set navigation direction
        navigationDirectionRef.current = 'backward';
        onPageChange(prevPage);
        // The effect will handle setting activeItemIndex when page changes
      }
      // Otherwise, we're at the first item of the first page - do nothing
    } else {
      // Just go to previous item on current page
      updateActiveItem(activeItemIndex - 1);
    }
  }, [isLoading, activeItemIndex, currentPage, onPageChange, updateActiveItem]);
  // Allow external control of active item
  const setActiveItemIndexExternal = useCallback(index => {
    if (index >= 0 && index < itemCount) {
      updateActiveItem(index);
    }
  }, [itemCount, updateActiveItem]);
  return {
    activeItemIndex,
    setActiveItemIndex: setActiveItemIndexExternal,
    gotoNextItem,
    gotoPreviousItem,
    isFirstItem: itemCount > 0 && activeItemIndex === 0 && currentPage === 1,
    isLastItem: itemCount > 0 && activeItemIndex === itemCount - 1 && currentPage === totalPages
  };
};

/**
 * Default helper function to recursively remove empty values from an object
 * Removes: null, undefined, empty strings, empty arrays, and objects with no remaining keys
 */
function defaultRemoveEmptyValues(obj) {
  if (obj === null || obj === undefined || obj === '') {
    return undefined;
  }
  if (Array.isArray(obj)) {
    if (obj.length === 0) return undefined;
    const filtered = obj.map(defaultRemoveEmptyValues).filter(v => v !== undefined);
    return filtered.length > 0 ? filtered : undefined;
  }
  if (obj instanceof Date) {
    return obj;
  }
  if (typeof obj === 'object') {
    const filtered = {};
    let hasKeys = false;
    Object.entries(obj).forEach(([key, value]) => {
      const cleanedValue = defaultRemoveEmptyValues(value);
      if (cleanedValue !== undefined) {
        filtered[key] = cleanedValue;
        hasKeys = true;
      }
    });
    return hasKeys ? filtered : undefined;
  }
  return obj;
}
/**
 * A generic hook for serializing and deserializing state to/from base64 encoded strings.
 * By default, automatically removes empty values (null, undefined, empty strings, empty arrays, empty objects)
 * when serializing to minimize the encoded string size.
 *
 * @example
 * ```tsx
 * interface MyState {
 *   filters: { category?: string; price?: number }
 *   sort: { field: string; order: 'asc' | 'desc' }
 * }
 *
 * const { serialize, deserialize } = useStateSerializer<MyState>()
 *
 * // Serialize state to base64 (using default empty value removal)
 * const encoded = serialize({ filters: { category: 'electronics' }, sort: { field: 'price', order: 'asc' } })
 *
 * // Deserialize back to state
 * const state = deserialize(encoded)
 *
 * // Use custom empty value removal function
 * const customRemoveEmpty = (obj: unknown) => {
 *   // Custom logic: only remove null and undefined, keep empty strings
 *   if (obj === null || obj === undefined) return undefined
 *   return obj
 * }
 * const encoded2 = serialize(myState, customRemoveEmpty)
 * ```
 */
const useStateSerializer = () => {
  const serialize = useCallback((state, removeEmptyValues) => {
    const cleanFunction = removeEmptyValues || defaultRemoveEmptyValues;
    const compactState = cleanFunction(state);
    const jsonString = JSON.stringify(compactState || {});
    return jsonString === '{}' ? '' : btoa(jsonString);
  }, []);
  const deserialize = useCallback(encoded => {
    try {
      const jsonString = atob(encoded);
      return jsonString === '' ? {} : JSON.parse(jsonString);
    } catch (error) {
      throw new Error(`Failed to deserialize state: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }, []);
  return {
    serialize,
    deserialize
  };
};

export { useInterval, useIsMounted, useMountEffect, useOnce, usePrevious, useRowNavigation, useStateSerializer, useTimeout, useUpdateEffect };
//# sourceMappingURL=hooks.js.map
